import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCSelectOptionsCard, RCCServicesApi, RCCSwitch} from "react-common-components";
import { useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { ISidebarCardItem } from "src/components/SidebarCard/interface";
import ModalSide from "src/components/Modal/styles";
import DateTimePickerRange from "src/components/DateTimePickerRange";

import { ITypeModal } from "src/components/Modal/interface";
import { IBudgetPlanModalCopy, IOftba001Form, IRefBudgetPlanModalsCopy } from "./interface";
import { copySchema } from "./schemas";

const ModalsCopy: React.ForwardRefRenderFunction<IRefBudgetPlanModalsCopy, IBudgetPlanModalCopy> = ({
	onCallback
},ref) => {
	const { translate } = useTranslation();
	const {addToast} = RCCHookToast();

	const [budgetPlanSelected, setBudgetPlanSelected] = useState<IOftba001Form>();

	// Modal Copy
	const modalCopy = useRef<RCCRefModalProps>(null);
	const { 
		handleSubmit : handleSubmitCopy, 
		register : registerCopy, 
		control: controlCopy, 
		reset : resetCopy, 
		setError : setErrorCopy, 
		formState: { errors : errorsCopy }, 
		clearErrors: clearErrorsCopy
	} = useForm();


	const actionButtonCopy = (card: ISidebarCardItem) => {
		resetCopy(); 
		const startDate = card.subtitle.slice(0, 10).split("/");
		const startYear = Number(startDate[2]);
		const endDate = card.subtitle.slice(-10).split("/");
		const endYear = Number(endDate[2]);

		setBudgetPlanSelected({
			ofa001Id: card.id,
			ofa001Nome:`(${translate("CÓPIA")}) - ${card.title}`,
			ofa001Dtinicio: card.subtitle ? new Date(startYear, 0, 1) : new Date(new Date().getFullYear(), 0, 1),
			ofa001Dtfim: card.subtitle ? new Date(endYear, 11, 31) : new Date(new Date().getFullYear(), 0, 1)
		} as IOftba001Form);	
		modalCopy.current?.openModal();
	};

	const submitCopy = async (data: any) => {
		try{
			clearErrorsCopy();
			modalCopy.current?.setLoadingButton();
			
			const dataForm = data;		
			dataForm.ofa001Rascunho = data.ofa001Rascunho ? "S" : "N";

			await copySchema(translate).validate({...dataForm}, {
				abortEarly: false
			});
			
			const result = await RCCServicesApi.postRequest({
				method: "FinancialManagement/CopyBudgetPlan",
				body: {
					oftba001: {
						ofa001Id: dataForm.ofa001Id,
						ofa001Dtinicio: dataForm.ofa001Dtinicio,
						ofa001Dtfim: dataForm.ofa001Dtfim,
						ofa001Nome: dataForm.ofa001Nome,
						ofa001Rascunho: dataForm.ofa001Rascunho
					},
					copyPermissions: dataForm.copyPermissions,
					copyPlanned: dataForm.copyPlanned,
					copyRealized: dataForm.copyRealized === "S"
				}
			});

			onCallback(result.data);

			addToast({
				color: "green",
				description: translate("Plano orcamentário copiado com sucesso"),
				title: translate("Sucesso")
			});

			modalCopy.current?.resetLoadingButton();
			modalCopy.current?.closeModal();

		}catch(err){

			modalCopy.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorCopy)){
				addToast({
					color: "yellow",
					description: translate("Verifique os campos e tente novamente"),
					title: translate("Atenção")
				});
				return;
			}
				
			standardErrorMessages(err, translate("Erro ao copiar plano orçamentário"), addToast);
		}
	};

	useImperativeHandle(ref, () => ({
		actionButtonCopy
	}));

	return (
		<Modal
			type={ITypeModal.COPY}
			ref={modalCopy}
			header={{
				title: translate("Copiar plano orçamentário"),
				subtitle: `${translate("Plano orçamentário")}: ${budgetPlanSelected?.ofa001Nome}`,
				icon: {
					name: "dollar"
				}
			}}
			onSubmit={handleSubmitCopy(submitCopy)}
			styleInline={ModalSide}
		>
			<RCCGrid
				container
				item
				direction="column"
				alignItems="center"
			>
				<input 
					type="text"
					value={budgetPlanSelected?.ofa001Id}
					{...registerCopy("ofa001Id")} 
					style={{ display: "none" }} 
					readOnly
				/>

				<RCCGrid
					container
					item
				>
					<RCCInput 
						register={registerCopy}
						name="ofa001Nome"
						label={translate("Nome")}
						maxLength={100}
						defaultValue={budgetPlanSelected && budgetPlanSelected?.ofa001Nome}
						error={errorsCopy.ofa001Nome}
						required
					/>
				</RCCGrid>

				<RCCGrid
					container
					item
				>
					<DateTimePickerRange
						control={controlCopy}
						type="datePicker"
						label={translate("Período")}
						labelSeparator={translate("Até")}
						nameInit="ofa001Dtinicio"
						nameEnd="ofa001Dtfim"
						defaultValueInit={budgetPlanSelected?.ofa001Dtinicio}
						defaultValueEnd={budgetPlanSelected?.ofa001Dtfim}
						error={errorsCopy.ofa001Dtinicio || errorsCopy.ofa001Dtfim}
						required
						props={{
							datePicker: {
								format: "yyyy",
								views: ["year"]
							}
						}}
					/>							
				</RCCGrid>

				<RCCGrid
					container
					item
				>
					<RCCSelectOptionsCard
						defaultValue="1"
						name="copyPlanned"
						options={[{
							label: translate("Planejado"),
							value: "1"
						},{
							label: translate("Realizado"),
							value: "2"
						},{
							label: translate("Não copiar"),
							value: "3"
						}]}
						label={translate("Valor planejado")}
						control={controlCopy}
					/>
				</RCCGrid>

				<RCCGrid
					container
					item
				>
					<RCCSelectOptionsCard
						defaultValue="S"
						name="copyRealized"
						options={[{
							label: translate("Realizado"),
							value: "S"
						},{
							label: translate("Não copiar"),
							value: "N"
						}]}
						label={translate("Valor realizado")}
						control={controlCopy}
					/>
				</RCCGrid>

				<RCCGrid 
					container
					item
					alignItems="center"
				>
					<RCCSwitch 
						name="copyPermissions" 
						label={translate("Permissões")} 
						register={registerCopy} 
						defaultChecked
					/>
				</RCCGrid>
				
				<RCCGrid 
					container
					item
					alignItems="center"
				>
					<RCCSwitch 
						name="ofa001Rascunho" 
						label={translate("Rascunho")} 
						register={registerCopy} 
					/>
				</RCCGrid>
				
			</RCCGrid>
		</Modal>
	);
};

export default forwardRef(ModalsCopy);
